import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  BulkBatchApprovalRequest,
  UpdateExternalTimesheetRequest,
} from 'src/api/FiretrolApiClient';
import { timesheetApiClient } from 'src/api/FiretrolApiClientInstances';
import { queryKeys } from 'src/api/queries/queryKeys';
import { invalidateFor } from 'src/api/queries/invalidationMap';
import { toCancelToken } from 'src/api/queries/queryClient';

// The review pages are unauthenticated: a 401 means the emailed link expired,
// and must not reach the global handler's re-auth redirect.
const EXTERNAL_META = {
  expectedStatuses: [401],
  suppressErrorToast: true,
} as const;

/** @registry One timesheet fetched through an emailed review token. */
export function useExternalTimesheet(
  timesheetId: string | null | undefined,
  token: string,
) {
  return useQuery({
    queryKey: queryKeys.timesheets.external(token, timesheetId ?? ''),
    queryFn: ({ signal }) =>
      timesheetApiClient.getExternalTimesheetById(
        timesheetId!,
        token,
        toCancelToken(signal),
      ),
    enabled: !!timesheetId,
    staleTime: 0,
    meta: { ...EXTERNAL_META, errorContext: 'external timesheet' },
  });
}

/** @registry Every timesheet in one emailed batch-review link. */
export function useExternalBatchReview(token: string | undefined) {
  return useQuery({
    queryKey: queryKeys.timesheets.externalBatch(token ?? ''),
    queryFn: ({ signal }) =>
      timesheetApiClient.getExternalBatchReview(token!, toCancelToken(signal)),
    enabled: !!token,
    staleTime: 0,
    meta: { ...EXTERNAL_META, errorContext: 'external batch review' },
  });
}

/** @registry Approves or rejects one timesheet from the external review page. */
export function useUpdateExternalTimesheet(token: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      timesheetId,
      request,
    }: {
      timesheetId: string;
      request: UpdateExternalTimesheetRequest;
    }) => timesheetApiClient.updateExternal(timesheetId, token, request),
    meta: { ...EXTERNAL_META, errorContext: 'external timesheet update' },
    onSuccess: (result, { timesheetId }) => {
      qc.setQueryData(
        queryKeys.timesheets.external(token, timesheetId),
        result,
      );
    },
    onSettled: () => invalidateFor('timesheet.externalReview', qc),
  });
}

/** @registry Approves or rejects a whole emailed timesheet batch. */
export function useUpdateExternalBatchReview(token: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (request: BulkBatchApprovalRequest) =>
      timesheetApiClient.updateExternalBatchReview(token, request),
    meta: { ...EXTERNAL_META, errorContext: 'external batch review update' },
    onSettled: () => invalidateFor('timesheet.externalReview', qc),
  });
}
